<script lang="ts">
  import { ethers } from '@credenza3/core-web-evm-ext'
  import { get } from 'svelte/store'
  import { onMount } from 'svelte'
  import { Pages } from '@packages/utils/enums'
  import { clientPublicStore, fn, pageOptsStore, pageStore, userStore } from '@packages/stores'
  import { dispatch, PassportEvents } from '@lib/events/events'
  import { TransactionTypes } from '@packages/utils/enums'
  import { sendContractTx, getTxScanAddress } from '@src/lib/tx/tx'
  import { requestReceipt } from '@pages/payment/Payment.service'
  import { analytics } from '@lib/mixpanel/mixpanel'
  import { waitVideoEnded } from '@components/progress/ProgressVideo.service'
  import { type TEvmContract, type TPaymentItem } from '@packages/utils/types'
  import { CredenzaBalance } from '@packages/ui'
  import { getAddress } from '@src/passport/blockchain'
  import { configStore, providerStore } from '@src/stores'
  import Loader from '@packages/ui/main/shared/Loader.svelte'
  import { abi as license_abi } from '@credenza3/contracts/artifacts/LicenseNftContract.json'
  import { DEFAULT_TOKEN_ID } from '@packages/utils/constants'

  export let totalPriceToken: number
  export let isLoading: boolean
  export let isPriceLoading: boolean
  export let pricesByToken: { [key: string]: number }

  const { getCREDContract, toastAlert } = get(fn)
  const { chainId, clientId } = get(configStore)

  let isBalanceLoading: boolean = false
  let balance: bigint = BigInt(0)
  let formattedBalance: string
  let symbol: string = ''
  let contract: ethers.Contract
  let decimals: number
  let userAddress: string
  let signer: ethers.Signer
  let credContract: {
    address: string
    decimals: number
    contract: ethers.Contract
  }

  const afterPayment = async (index: number, result: ethers.TransactionResponse, priceToken: number) => {
    toastAlert(`<a href="${getTxScanAddress(result.hash)}" target="_blank">Purchase TX #${index}</a> was created`)
    dispatch(PassportEvents.PAYMENT, { type: TransactionTypes.ERC20, data: result })
    dispatch(PassportEvents.RECHECK_BALANCE)

    await requestReceipt({
      hash: result.hash,
      title: $pageOptsStore.title,
      subtitle: $pageOptsStore.subtitle,
      currency: 'CRED',
      amount: +ethers.formatUnits(priceToken, decimals),
    })
    dispatch(PassportEvents.RECEIPT, { hash: result.hash })
  }

  const processPaymentItem = async (item: TPaymentItem, index: number) => {
    try {
      const { credenzaSellableMinAbi } = await import('@src/lib/abi')
      const credenzaSellableContract = new ethers.Contract(item.contractAddress, credenzaSellableMinAbi, signer)
      const tokenId = item.tokenId || item.membershipTokenId || DEFAULT_TOKEN_ID
      const priceToken = pricesByToken[`${item.contractAddress}/${tokenId}`]

      const approveTx = await contract.approve.populateTransaction(
        await credenzaSellableContract.getAddress(),
        priceToken,
      )

      await sendContractTx(approveTx, contract)
      const buyTx = await credenzaSellableContract.buyWithToken.populateTransaction(
        tokenId,
        item.amount || 1,
        userAddress,
      )

      const result = await sendContractTx(buyTx, credenzaSellableContract)

      await afterPayment(index, result, priceToken)
    } catch (err) {
      toastAlert(`Payment error: ${err.message || err}`, 'failure')
      pageOptsStore.set({ errorMessage: err.message })
      pageStore.set(Pages.ERROR)
      dispatch(PassportEvents.ERROR, { error: err })
    }
  }

  const processLicensePaymentItem = async (item: TPaymentItem, index: number) => {
    if (!item.licenseContractAddress) return

    try {
      const licenseContract = new ethers.Contract(item.licenseContractAddress, license_abi, signer)
      const priceToken = pricesByToken[`${item.contractAddress}/${item.contentTokenId}`]

      const approveTx = await contract.approve.populateTransaction(
        await licenseContract.getAddress(),
        priceToken,
      )

      await sendContractTx(approveTx, contract)
      await new Promise(resolve => setTimeout(resolve, 3000)); // pause for debugging

      const buyLicenseWithTokenFunction = licenseContract.interface.getFunction('buyLicenseWithToken', ['uint', 'address', 'uint']);
      if (!buyLicenseWithTokenFunction) throw new Error('buyLicenseWithToken function not found');
      const buyTx =await licenseContract[
        buyLicenseWithTokenFunction.format()
      ].populateTransaction(
        item.contentTokenId,
        userAddress,
        item.licenseType
      )

      const result = await sendContractTx(buyTx, licenseContract)
      await afterPayment(index, result, priceToken)
    } catch (err) {
      toastAlert(`Payment error: ${err.message || err}`, 'failure')
      pageOptsStore.set({ errorMessage: err.message })
      pageStore.set(Pages.ERROR)
      dispatch(PassportEvents.ERROR, { error: err })
    }
  }

  const payWithCredenza = async () => {
    try {
      if (BigInt(totalPriceToken) > balance) return

      isLoading = true
      const tokensAndMembershipsPaymentPromises = [...($pageOptsStore.tokens || []), ...($pageOptsStore.memberships || [])].map(
        (item, index) => processPaymentItem(item, index + 1),
      )

      const licensesPaymentPromises = [...($pageOptsStore.licenses || [])].map(
        (item, index) => processLicensePaymentItem(item, index + 1),
      )

      await Promise.all([...tokensAndMembershipsPaymentPromises, ...licensesPaymentPromises])

      const user = get(userStore)
      if (user) {
        analytics.track('cp_payment_stored_value', {
          $email: user.email,
          $phone: user.phone,
          chain: chainId,
          clientId,
          address: userAddress,
          location: window.location.href,
          tokens: $pageOptsStore.tokens,
          memberships: $pageOptsStore.memberships,
          total: totalPriceToken,
          description: ($pageOptsStore?.title ?? '') + `(${$pageOptsStore?.subtitle ?? ''})`,
        })
      }

      await waitVideoEnded()

      if (totalPriceToken === 0) pageOptsStore.set({ subtitle: 'Your claim was successful!', receipt: false })
      pageStore.set(Pages.PAYMENT_RESULT)
    } catch (err) {
      toastAlert(`Payment error: ${err.message || err}`, 'failure')
      pageOptsStore.set({ errorMessage: err.message })
      pageStore.set(Pages.ERROR)
      dispatch(PassportEvents.ERROR, { error: err })
    } finally {
      isLoading = false
    }
  }

  onMount(async () => {
    try {
      isBalanceLoading = true
      credContract = (await getCREDContract()) as TEvmContract

      const provider = get(providerStore)
      if(!provider) return
      signer = await provider.getSigner()
      contract = credContract.contract
      decimals = credContract.decimals
      ;[userAddress, symbol] = await Promise.all([getAddress(), contract.symbol()])
      balance = await contract.balanceOf(userAddress)

      formattedBalance = `${ethers.formatUnits(BigInt(balance) - BigInt(totalPriceToken), decimals)}`
    } catch (err) {
      console.log(err)
    } finally {
      isBalanceLoading = false
    }
  })
</script>

{#if isBalanceLoading}
  <div class="crtw-w-full crtw-justify-items-center crtw-my-5">
    <Loader />
  </div>
{:else}
  <CredenzaBalance
    options={{
      formattedBalance,
      balance: Number((+ethers.formatUnits(BigInt(balance), decimals)).toFixed(2)),
      credAlias: $clientPublicStore?.ui_settings.cred_alias,
      onPay: payWithCredenza,
      onGetCred: () => pageStore.set(Pages.WALLET),
      totalToPay: (+ethers.formatUnits(totalPriceToken, decimals)).toFixed(2),
      isLoading,
      isPaymentDisabled: !Number(balance) || BigInt(totalPriceToken) > balance || isPriceLoading,
    }}
  />
{/if}
